import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { LANGS, CONTACT, COMPANY } from "@/i18n/translations";
import { HOME } from "@/i18n/home";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { ArrowLeft } from "lucide-react";

const Row = ({ label, children, testid }) => (
  <div className="grid sm:grid-cols-3 gap-1 sm:gap-6 py-4 border-b border-slate-100" data-testid={testid}>
    <dt className="text-sm font-semibold text-[#0A0F14]">{label}</dt>
    <dd className="sm:col-span-2 text-[15px] text-[#475569]">{children}</dd>
  </div>
);

export default function Legal() {
  const { lang, setLang, t } = useLang();
  const L = t.legal;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative min-h-screen bg-white" data-testid="legal-page">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto max-w-[1000px] px-5 sm:px-8 flex items-center justify-between h-[68px]">
          <Link to="/" data-testid="legal-logo-link" aria-label="Home">
            <Logo />
          </Link>
          <div className="flex items-center gap-4">
            <div className="flex items-center rounded-full border border-slate-200 bg-white/60 p-0.5">
              {LANGS.map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  data-testid={`legal-lang-${l}`}
                  className="px-2.5 py-1 text-xs font-semibold uppercase rounded-full transition-colors"
                  style={{ background: lang === l ? "var(--pt-ink)" : "transparent", color: lang === l ? "#fff" : "#475569" }}
                >
                  {l}
                </button>
              ))}
            </div>
            <Link to="/" data-testid="legal-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#475569] hover:text-[#0A0F14] transition-colors">
              <ArrowLeft size={16} /> <span className="hidden sm:inline">{L.back}</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1000px] px-5 sm:px-8 py-16 sm:py-24">
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          id="impressum"
          data-testid="impressum-section"
        >
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3BB0E5] mb-4">{COMPANY.name}</p>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">{L.impressum.title}</h1>
          <p className="mt-5 text-base text-[#475569]">{L.impressum.intro}</p>

          <dl className="mt-10">
            <Row label={L.impressum.companyLabel} testid="impressum-company">{COMPANY.name}</Row>
            <Row label={L.impressum.ownerLabel} testid="impressum-owner">{COMPANY.owner}</Row>
            <Row label={L.impressum.addressLabel} testid="impressum-address">{COMPANY.address}, {HOME[lang].locations.country}</Row>
            <Row label={L.impressum.vatLabel} testid="impressum-vat">{COMPANY.vat}</Row>
            <Row label={L.impressum.cfLabel} testid="impressum-cf">{COMPANY.cf}</Row>
            <Row label={L.impressum.phoneLabel} testid="impressum-phone">
              <a href={`tel:${CONTACT.phoneRaw}`} className="pt-link-underline">{CONTACT.phone}</a>
            </Row>
            <Row label={L.impressum.emailLabel} testid="impressum-email">
              <a href={`mailto:${CONTACT.email}`} className="pt-link-underline">{CONTACT.email}</a>
            </Row>
            <Row label={L.impressum.pecLabel} testid="impressum-pec">
              <a href={`mailto:${COMPANY.pec}`} className="pt-link-underline">{COMPANY.pec}</a>
            </Row>
          </dl>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          id="datenschutz"
          data-testid="privacy-section"
          className="mt-24 scroll-mt-24"
        >
          <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">{L.privacy.title}</h2>
          <div className="mt-10 space-y-9">
            {L.privacy.sections.map((s, i) => (
              <div key={i} data-testid={`privacy-block-${i}`}>
                <h3 className="text-lg font-semibold text-[#0A0F14]">{s.h}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[#475569]">{s.p}</p>
              </div>
            ))}
          </div>
        </motion.section>
      </main>

      <Footer />
    </div>
  );
}
